import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { History } from 'lucide-react';
import ExchangeCarInvoice from '../../components/ExchangeCarInvoice';
import { api } from '../../lib/api';
import { toast } from 'sonner';

// Validation schema for exchange car form
const exchangeCarFormSchema = z.object({
  // PersonOne - Car Information
  personOneCarBrand: z.string().min(1, 'Car brand is required'),
  personOneCarModel: z.string().min(1, 'Car model is required'),
  personOneCarYear: z.number().min(1900).max(new Date().getFullYear() + 1),
  personOneCarPlateNo: z.string().min(1, 'Plate number is required'),
  personOneCarChassisNo: z.string().optional(),
  personOneCarColor: z.string().min(1, 'Color is required'),

  // PersonOne - Personal Information
  personOneName: z.string().min(1, 'Name is required'),
  personOneIdNo: z.string().min(1, 'ID number is required'),
  personOneAddress: z.string().optional(),
  personOneContact: z.string().min(1, 'Contact is required'),

  // PersonTwo - Car Information
  personTwoCarBrand: z.string().min(1, 'Car brand is required'),
  personTwoCarModel: z.string().min(1, 'Car model is required'),
  personTwoCarYear: z.number().min(1900).max(new Date().getFullYear() + 1),
  personTwoCarPlateNo: z.string().min(1, 'Plate number is required'),
  personTwoCarChassisNo: z.string().optional(),
  personTwoCarColor: z.string().min(1, 'Color is required'),

  // PersonTwo - Personal Information
  personTwoName: z.string().min(1, 'Name is required'),
  personTwoIdNo: z.string().min(1, 'ID number is required'),
  personTwoAddress: z.string().optional(),
  personTwoContact: z.string().min(1, 'Contact is required'),

  // Exchange Information
  amountOwed: z.number().min(0),
  paidAmount: z.number().min(0),
  notes: z.string().optional()
});

type ExchangeCarFormData = z.infer<typeof exchangeCarFormSchema>;

interface ExchangeCar {
  id: number;
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string | null;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress: string | null;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string | null;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress: string | null;
  personTwoContact: string;
  amountOwed: number;
  paidAmount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function ExchangeCar() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [exchangedCar, setExchangedCar] = useState<ExchangeCar | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    clearErrors
  } = useForm<ExchangeCarFormData>({
    resolver: zodResolver(exchangeCarFormSchema),
    defaultValues: {
      personOneCarBrand: '',
      personOneCarModel: '',
      personOneCarYear: new Date().getFullYear(),
      personOneCarPlateNo: '',
      personOneCarChassisNo: '',
      personOneCarColor: '',
      personOneName: '',
      personOneIdNo: '',
      personOneAddress: '',
      personOneContact: '',
      personTwoCarBrand: '',
      personTwoCarModel: '',
      personTwoCarYear: new Date().getFullYear(),
      personTwoCarPlateNo: '',
      personTwoCarChassisNo: '',
      personTwoCarColor: '',
      personTwoName: '',
      personTwoIdNo: '',
      personTwoAddress: '',
      personTwoContact: '',
      amountOwed: 0,
      paidAmount: 0,
      notes: ''
    }
  });

  // Print functionality
  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `Exchange Car Invoice - ${exchangedCar?.personOneCarBrand} ${exchangedCar?.personOneCarModel} & ${exchangedCar?.personTwoCarBrand} ${exchangedCar?.personTwoCarModel}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; color-adjust: exact; }
      }
    `,
    onAfterPrint: () => {
      // Cleanup only after the browser finishes preparing/closing print
      reset({
        personOneCarBrand: '',
        personOneCarModel: '',
        personOneCarYear: new Date().getFullYear(),
        personOneCarPlateNo: '',
        personOneCarChassisNo: '',
        personOneCarColor: '',
        personOneName: '',
        personOneIdNo: '',
        personOneAddress: '',
        personOneContact: '',
        personTwoCarBrand: '',
        personTwoCarModel: '',
        personTwoCarYear: new Date().getFullYear(),
        personTwoCarPlateNo: '',
        personTwoCarChassisNo: '',
        personTwoCarColor: '',
        personTwoName: '',
        personTwoIdNo: '',
        personTwoAddress: '',
        personTwoContact: '',
        amountOwed: 0,
        paidAmount: 0,
        notes: ''
      });
      setShowInvoice(false);
    }
  });

  // Clear form function
  const clearForm = () => {
    reset({
      personOneCarBrand: '',
      personOneCarModel: '',
      personOneCarYear: new Date().getFullYear(),
      personOneCarPlateNo: '',
      personOneCarChassisNo: '',
      personOneCarColor: '',
      personOneName: '',
      personOneIdNo: '',
      personOneAddress: '',
      personOneContact: '',
      personTwoCarBrand: '',
      personTwoCarModel: '',
      personTwoCarYear: new Date().getFullYear(),
      personTwoCarPlateNo: '',
      personTwoCarChassisNo: '',
      personTwoCarColor: '',
      personTwoName: '',
      personTwoIdNo: '',
      personTwoAddress: '',
      personTwoContact: '',
      amountOwed: 0,
      paidAmount: 0,
      notes: ''
    });
    setSubmitted(false);
    setExchangedCar(null);
    setShowInvoice(false);
  };

  // Handle clear form with confirmation
  const handleClearForm = () => {
    clearForm();
  };

  const onSubmit = async (data: ExchangeCarFormData) => {
    try {
      setLoading(true);

      // Clean phone numbers by removing spaces
      const cleanedData = {
        ...data,
        personOneContact: data.personOneContact?.replace(/\s/g, '') || data.personOneContact,
        personTwoContact: data.personTwoContact?.replace(/\s/g, '') || data.personTwoContact
      };

      // Create the exchange car record
      const exchangeCarData = await api.post('/api/exchange-cars', cleanedData);
      setExchangedCar(exchangeCarData);
      setShowInvoice(true);
      setSubmitted(true);

      // Wait for invoice to mount + layout before printing (prevents multi-page glitches)
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      handlePrint();
    } catch (error) {
      console.error('Error exchanging cars:', error);
      toast.error('Failed to record car exchange');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('exchangeCar.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('exchangeCar.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/exchange-cars">
              <Button variant="outline" size="sm">
                <History className="h-4 w-4 mr-2" />
                {t('exchangeCar.history')}
              </Button>
            </Link>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  {t('exchangeCar.clear')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('exchangeCar.clearForm')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('exchangeCar.clearFormConfirm')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('exchangeCar.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClearForm} className="bg-red-600 hover:bg-red-700">
                    {t('exchangeCar.clearForm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
              <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
              <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {submitted && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {t('exchangeCar.success')}
          </div>
        )}

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* PersonOne Section */}
            <div className="space-y-4 border-2 border-cyan-200 rounded-lg p-6 bg-cyan-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-cyan-300 pb-2">
                {t('exchangeCar.personOne')}
              </h3>

              {/* PersonOne Car Details */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.carDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarBrand">{t('exchangeCar.carBrand')} *</Label>
                    <Input
                      id="personOneCarBrand"
                      type="text"
                      placeholder="e.g., Toyota"
                      {...register('personOneCarBrand', {
                        onChange: () => clearErrors('personOneCarBrand')
                      })}
                      className={errors.personOneCarBrand ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarBrand && (
                      <p className="text-red-500 text-sm">{errors.personOneCarBrand.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarModel">{t('exchangeCar.carModel')} *</Label>
                    <Input
                      id="personOneCarModel"
                      type="text"
                      placeholder="e.g., Camry"
                      {...register('personOneCarModel', {
                        onChange: () => clearErrors('personOneCarModel')
                      })}
                      className={errors.personOneCarModel ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarModel && (
                      <p className="text-red-500 text-sm">{errors.personOneCarModel.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarYear">{t('exchangeCar.carYear')} *</Label>
                    <Input
                      id="personOneCarYear"
                      type="number"
                      placeholder="2024"
                      {...register('personOneCarYear', {
                        valueAsNumber: true,
                        onChange: () => clearErrors('personOneCarYear')
                      })}
                      className={errors.personOneCarYear ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarYear && (
                      <p className="text-red-500 text-sm">{errors.personOneCarYear.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarPlateNo">{t('exchangeCar.plateNumber')} *</Label>
                    <Input
                      id="personOneCarPlateNo"
                      type="text"
                      placeholder="Plate number"
                      {...register('personOneCarPlateNo', {
                        onChange: () => clearErrors('personOneCarPlateNo')
                      })}
                      className={errors.personOneCarPlateNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarPlateNo && (
                      <p className="text-red-500 text-sm">{errors.personOneCarPlateNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarColor">{t('exchangeCar.carColor')} *</Label>
                    <Input
                      id="personOneCarColor"
                      type="text"
                      placeholder="e.g., Silver"
                      {...register('personOneCarColor', {
                        onChange: () => clearErrors('personOneCarColor')
                      })}
                      className={errors.personOneCarColor ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneCarColor && (
                      <p className="text-red-500 text-sm">{errors.personOneCarColor.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneCarChassisNo">{t('exchangeCar.chassisNo')}</Label>
                    <Input
                      id="personOneCarChassisNo"
                      type="text"
                      placeholder="Chassis number (optional)"
                      {...register('personOneCarChassisNo')}
                    />
                  </div>
                </div>
              </div>

              {/* PersonOne Personal Details */}
              <div className="space-y-4 mt-6">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.personalDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personOneName">{t('exchangeCar.name')} *</Label>
                    <Input
                      id="personOneName"
                      type="text"
                      placeholder="Full name"
                      {...register('personOneName', {
                        onChange: () => clearErrors('personOneName')
                      })}
                      className={errors.personOneName ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneName && (
                      <p className="text-red-500 text-sm">{errors.personOneName.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneIdNo">{t('exchangeCar.idNo')} *</Label>
                    <Input
                      id="personOneIdNo"
                      type="text"
                      placeholder="ID number"
                      {...register('personOneIdNo', {
                        onChange: () => clearErrors('personOneIdNo')
                      })}
                      className={errors.personOneIdNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneIdNo && (
                      <p className="text-red-500 text-sm">{errors.personOneIdNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneAddress">{t('exchangeCar.address')}</Label>
                    <Input
                      id="personOneAddress"
                      type="text"
                      placeholder="Address (optional)"
                      {...register('personOneAddress', {
                        onChange: () => clearErrors('personOneAddress')
                      })}
                      className={errors.personOneAddress ? 'border-red-500 focus:border-red-500' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personOneContact">{t('exchangeCar.contact')} *</Label>
                    <Input
                      id="personOneContact"
                      type="text"
                      placeholder="Contact number"
                      {...register('personOneContact', {
                        onChange: () => clearErrors('personOneContact')
                      })}
                      className={errors.personOneContact ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personOneContact && (
                      <p className="text-red-500 text-sm">{errors.personOneContact.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PersonTwo Section */}
            <div className="space-y-4 border-2 border-emerald-200 rounded-lg p-6 bg-emerald-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-emerald-300 pb-2">
                {t('exchangeCar.personTwo')}
              </h3>

              {/* PersonTwo Car Details */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.carDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarBrand">{t('exchangeCar.carBrand')} *</Label>
                    <Input
                      id="personTwoCarBrand"
                      type="text"
                      placeholder="e.g., Honda"
                      {...register('personTwoCarBrand', {
                        onChange: () => clearErrors('personTwoCarBrand')
                      })}
                      className={errors.personTwoCarBrand ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarBrand && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarBrand.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarModel">{t('exchangeCar.carModel')} *</Label>
                    <Input
                      id="personTwoCarModel"
                      type="text"
                      placeholder="e.g., Accord"
                      {...register('personTwoCarModel', {
                        onChange: () => clearErrors('personTwoCarModel')
                      })}
                      className={errors.personTwoCarModel ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarModel && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarModel.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarYear">{t('exchangeCar.carYear')} *</Label>
                    <Input
                      id="personTwoCarYear"
                      type="number"
                      placeholder="2024"
                      {...register('personTwoCarYear', {
                        valueAsNumber: true,
                        onChange: () => clearErrors('personTwoCarYear')
                      })}
                      className={errors.personTwoCarYear ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarYear && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarYear.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarPlateNo">{t('exchangeCar.plateNumber')} *</Label>
                    <Input
                      id="personTwoCarPlateNo"
                      type="text"
                      placeholder="Plate number"
                      {...register('personTwoCarPlateNo', {
                        onChange: () => clearErrors('personTwoCarPlateNo')
                      })}
                      className={errors.personTwoCarPlateNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarPlateNo && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarPlateNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarColor">{t('exchangeCar.carColor')} *</Label>
                    <Input
                      id="personTwoCarColor"
                      type="text"
                      placeholder="e.g., Black"
                      {...register('personTwoCarColor', {
                        onChange: () => clearErrors('personTwoCarColor')
                      })}
                      className={errors.personTwoCarColor ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoCarColor && (
                      <p className="text-red-500 text-sm">{errors.personTwoCarColor.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoCarChassisNo">{t('exchangeCar.chassisNo')}</Label>
                    <Input
                      id="personTwoCarChassisNo"
                      type="text"
                      placeholder="Chassis number (optional)"
                      {...register('personTwoCarChassisNo')}
                    />
                  </div>
                </div>
              </div>

              {/* PersonTwo Personal Details */}
              <div className="space-y-4 mt-6">
                <h4 className="text-lg font-semibold text-gray-700">{t('exchangeCar.personalDetails')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="personTwoName">{t('exchangeCar.name')} *</Label>
                    <Input
                      id="personTwoName"
                      type="text"
                      placeholder="Full name"
                      {...register('personTwoName', {
                        onChange: () => clearErrors('personTwoName')
                      })}
                      className={errors.personTwoName ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoName && (
                      <p className="text-red-500 text-sm">{errors.personTwoName.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoIdNo">{t('exchangeCar.idNo')} *</Label>
                    <Input
                      id="personTwoIdNo"
                      type="text"
                      placeholder="ID number"
                      {...register('personTwoIdNo', {
                        onChange: () => clearErrors('personTwoIdNo')
                      })}
                      className={errors.personTwoIdNo ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoIdNo && (
                      <p className="text-red-500 text-sm">{errors.personTwoIdNo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoAddress">{t('exchangeCar.address')}</Label>
                    <Input
                      id="personTwoAddress"
                      type="text"
                      placeholder="Address (optional)"
                      {...register('personTwoAddress', {
                        onChange: () => clearErrors('personTwoAddress')
                      })}
                      className={errors.personTwoAddress ? 'border-red-500 focus:border-red-500' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personTwoContact">{t('exchangeCar.contact')} *</Label>
                    <Input
                      id="personTwoContact"
                      type="text"
                      placeholder="Contact number"
                      {...register('personTwoContact', {
                        onChange: () => clearErrors('personTwoContact')
                      })}
                      className={errors.personTwoContact ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {errors.personTwoContact && (
                      <p className="text-red-500 text-sm">{errors.personTwoContact.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Exchange Information */}
            <div className="space-y-4 border-2 border-teal-200 rounded-lg p-6 bg-teal-50/50">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-teal-300 pb-2">
                {t('exchangeCar.exchangeInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="amountOwed">{t('exchangeCar.amountOwed')}</Label>
                  <Input
                    id="amountOwed"
                    type="number"
                    placeholder="0"
                    step="0.01"
                    {...register('amountOwed', {
                      valueAsNumber: true,
                      onChange: () => clearErrors('amountOwed')
                    })}
                    className={errors.amountOwed ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  <p className="text-sm text-gray-600">{t('exchangeCar.amountOwedHint')}</p>
                  {errors.amountOwed && (
                    <p className="text-red-500 text-sm">{errors.amountOwed.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paidAmount">{t('exchangeCar.paidAmount')}</Label>
                  <Input
                    id="paidAmount"
                    type="number"
                    placeholder="0"
                    step="0.01"
                    {...register('paidAmount', {
                      valueAsNumber: true,
                      onChange: () => clearErrors('paidAmount')
                    })}
                    className={errors.paidAmount ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  <p className="text-sm text-gray-600">{t('exchangeCar.paidAmountHint')}</p>
                  {errors.paidAmount && (
                    <p className="text-red-500 text-sm">{errors.paidAmount.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t('exchangeCar.notes')}</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Additional notes (optional)"
                    {...register('notes', {
                      onChange: () => clearErrors('notes')
                    })}
                    className={errors.notes ? 'border-red-500 focus:border-red-500' : ''}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-semibold px-8 py-3"
              >
                {isSubmitting ? t('exchangeCar.submitting') : t('exchangeCar.submit')}
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Invoice Component */}
      {showInvoice && exchangedCar && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
          <ExchangeCarInvoice
            ref={invoiceRef}
            exchangeCar={exchangedCar}
            isReprint={false}
          />
        </div>
      )}
    </div>
  );
}

